import { useQuery } from '@tanstack/react-query'
import {
  DataTable,
  DataTableProvider,
  useDataTableContext,
} from 'components/DataTable'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import * as S from 'components/Tables/CommonTable.styles'
import { ContactResponse } from 'models/contacts'
import React, { useCallback, useEffect, useMemo } from 'react'

import apiService from 'services/api'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { FilterIdentifier } from '../../../models/saved_view'

import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { DataTableContainer } from '../../DataTable/UI'
import { IColumnVisibility } from '../../DataTable/types'

import { TableSearch } from '../../Filters/TableSearch'
import { FilterChips } from '../../Filters/components/FilterChips/FilterChips'
import { cn } from '../../UI/cn'
import { DealsFilterset } from './SelectLocationsForDealFilterset'
import { useSelectLocationsForDealTableColumn } from './useSelectLocationsForDealTableColumn'
import FbButton from 'components/FbUI/FbButton'
import { BsPlus } from 'react-icons/bs'

type SortableFields = (keyof ContactResponse | string)[]

const sortableFields: SortableFields = [
  'id',
  'name',
  'address',
  'city',
  'state',
  'zipcode',
  'dma',
  'domain',
]

const PAGE_SIZE = 100

interface SelectLocationsForDealsTableProps {
  setTotalRowsCount?: (count: number) => void
  chainProxyId: number
  baseFilters?: Record<string, any>
  tableKey: string
  filterIdentifierModifier?: string
  type: 'selected' | 'not_selected'
  selectedIds: number[]
  onSelectedIdsChange: (ids: number[]) => void
}

function SelectLocationsForDealTableComponent(
  props: SelectLocationsForDealsTableProps
) {
  const api = apiService()

  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()
  const {
    state: { totalSelectedRows, rowSelection, isAllRowsSelected },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()

  const IDENTIFIER = ('SELECT_LOCATIONS' +
    (props.filterIdentifierModifier ?? '')) as FilterIdentifier
  const selectLocationsForDealFilterStore = filterStoreRepo.getStore(
    IDENTIFIER,
    true
  )
  const { page, setPage } = selectLocationsForDealFilterStore()
  const filtersAsParams = {
    ...props.baseFilters,
    ...useFilterParams(selectLocationsForDealFilterStore),
  }

  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = { ...filtersAsParams }

    if (props.type === 'selected') {
      params['ids'] = props.selectedIds
    } else {
      params['exclude_ids'] = props.selectedIds
    }

    return params
  }, [filtersAsParams, props.selectedIds, props.type])

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
    }),
    [filterParams, sortParams]
  )

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    page: page,
    extra: {
      chainProxyId: props.chainProxyId,
    },
  })

  // DATA FETCHING
  const { isFetching, data } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: ({ signal }) => {
      clearSelectedRows()
      if (
        Array.isArray(filterAndSortParams?.['ids']) &&
        filterAndSortParams['ids'].length === 0
      ) {
        return {
          results: [],
          count: 0,
          next: '',
          previous: '',
        }
      }
      const { filterAndSortParamsWithoutIds, body } =
        extractBodyFromFilterandSortParams()
      return api.getDoorsForSelection(
        props.chainProxyId,
        {
          ...filterAndSortParamsWithoutIds,
          page: page,
          limit: PAGE_SIZE,
        },
        body,
        signal
      )
    },
  })

  useEffect(() => {
    if (data?.count !== undefined) {
      props.setTotalRowsCount?.(data.count)
      setTotalRowsInBackend(data.count)
    }
  }, [data])

  const columns = useSelectLocationsForDealTableColumn()

  const selectedDoorIds = useMemo(() => {
    const selectedRows = data?.results?.filter((_, i) => rowSelection[i])
    return selectedRows?.map((row) => row.id) ?? []
  }, [data?.results, rowSelection])

  const unselectedDoorIds = useMemo(() => {
    const unselectedRows = data?.results?.filter((_, i) => !rowSelection[i])
    return unselectedRows?.map((row) => row.id) ?? []
  }, [data?.results, rowSelection])

  const handleSelectDoors = useCallback(async () => {
    let allIdsToAddToSelected = [...selectedDoorIds]
    if (isAllRowsSelected) {
      const { filterAndSortParamsWithoutIds, body } =
        extractBodyFromFilterandSortParams()
      const response = await api.getDoorsForDealSelectionIdsOnly(
        props.chainProxyId,
        {
          ...filterAndSortParamsWithoutIds,
        },
        body
      )
      allIdsToAddToSelected = [...response.ids, ...selectedDoorIds].filter(
        (id) => !unselectedDoorIds.includes(id)
      )
    }
    props.onSelectedIdsChange([
      ...new Set([...props.selectedIds, ...allIdsToAddToSelected]),
    ])
  }, [
    selectedDoorIds,
    isAllRowsSelected,
    unselectedDoorIds,
    extractBodyFromFilterandSortParams,
    props.onSelectedIdsChange,
    props.selectedIds,
  ])

  const handleUnselectDoors = useCallback(async () => {
    let allIdsToRemoveFromSelected = [...selectedDoorIds]
    if (isAllRowsSelected) {
      const { filterAndSortParamsWithoutIds, body } =
        extractBodyFromFilterandSortParams()
      const response = await api.getDoorsForDealSelectionIdsOnly(
        props.chainProxyId,
        {
          ...filterAndSortParamsWithoutIds,
        },
        body
      )
      allIdsToRemoveFromSelected = [
        ...response.ids,
        ...unselectedDoorIds,
      ].filter((id) => !unselectedDoorIds.includes(id))
    }
    props.onSelectedIdsChange(
      props.selectedIds.filter((id) => !allIdsToRemoveFromSelected.includes(id))
    )
  }, [
    selectedDoorIds,
    isAllRowsSelected,
    unselectedDoorIds,
    extractBodyFromFilterandSortParams,
    props.onSelectedIdsChange,
    props.selectedIds,
  ])

  const [isActionLoading, setIsActionLoading] = React.useState(false)

  const handleActionClick = async () => {
    setIsActionLoading(true)
    if (props.type === 'selected') {
      await handleUnselectDoors()
    } else {
      await handleSelectDoors()
    }
    setIsActionLoading(false)
  }

  return (
    <DataTableContainer
      key={props.tableKey + '-container'}
      className="h-[400px] flex"
    >
      <DataTable
        tableHeader={
          <S.SearchContainer>
            <div className="flex w-full gap-2 items-center">
              <TableSearch
                className={'flex-1'}
                filterStore={selectLocationsForDealFilterStore}
                searchPlaceholder="Search by name..."
                tableKey={props.tableKey}
              />
              <DealsFilterset
                filterIdentifier={IDENTIFIER}
                hideCampaignsFilter={props.baseFilters?.origin_campaign}
              />
            </div>

            <div className={cn('flex flex-row justify-between gap-4')}>
              {totalSelectedRows > 0 && (
                <FbButton
                  onClick={handleActionClick}
                  loading={isActionLoading}
                  type="button"
                  className="mt-2"
                >
                  <BsPlus size={20} />{' '}
                  {props.type === 'not_selected'
                    ? 'Add Locations'
                    : 'Remove Locations'}{' '}
                  ({totalSelectedRows})
                </FbButton>
              )}
              <FilterChips
                identifier={IDENTIFIER}
                store={selectLocationsForDealFilterStore}
                showActive
                clearAllButton
              />
            </div>
          </S.SearchContainer>
        }
        key={props.tableKey}
        tableKey={props.tableKey}
        loading={isFetching}
        data={data?.results ?? []}
        columns={columns}
        sortableFields={sortableFields}
        virtualizeRows={false}
        enableRowSelection
        isPaginationEnabled
        paginationOptions={{
          totalRows: data?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        selectAllText="Select Doors"
      />
    </DataTableContainer>
  )

  function extractBodyFromFilterandSortParams() {
    const body = {
      ids: filterAndSortParams['ids'] as number[] | undefined,
      exclude_ids: filterAndSortParams['exclude_ids'] as number[] | undefined,
    }
    const filterAndSortParamsWithoutIds = {
      ...filterAndSortParams,
    }
    delete filterAndSortParamsWithoutIds['ids']
    delete filterAndSortParamsWithoutIds['exclude_ids']
    return { filterAndSortParamsWithoutIds, body }
  }
}

export function SelectLocationsForDealTable(
  props: SelectLocationsForDealsTableProps & {
    defaultColumnVisibility?: IColumnVisibility<unknown>
  }
) {
  const { tableKey, defaultColumnVisibility, ...rest } = props
  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      defaultColumnVisibility={defaultColumnVisibility}
    >
      <DataTableProvider tableKey={tableKey}>
        <SelectLocationsForDealTableComponent tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
